import * as React from 'react';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import {
  Modal,
  Form,
  Input,
  Row,
  Col,
  Radio,
  Button,
  Select,
  DatePicker,
  message,
  Upload,
} from 'antd';
import moment from 'moment';
import { LoadingOutlined, PaperClipOutlined } from '@ant-design/icons';
import { isValidMobilePhoneNumber } from 'src/utils/utils';
import { addRiderLocationFields, addDriverFieldsList, genderList, addRiderTailLayout, addRiderFormItemLayout, VendorDetails, HubDetails, IdentificationDocument } from './AddRiderModal.utils';
import { AddDriverStyle } from './AddNewDriverModal.styles';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import { getS3SignedUrlAndUploadImage, getSignedUrlApi } from 'src/api/s3Upload';
import { useDebounce } from 'src/library/hooks/useDebounce';
import { ReadWithSignedUrl } from '../../../common/ReadWithSignedUrl';
import { addDriver, updateDriver, fetchDriverInfo, getAddressHierarchyData, getBottomUpHierarchyForAddressNode, fetchAddressMetadata } from './AssignedDrivers.api';
import {
  AddNewDriverModalProps,
  ImageUploaderProps,
  FetchRiderDetails,
} from 'src/components/pages/AssignedDrivers/AssignedDrivers.utils';
import { useTranslation } from 'react-i18next';
import lodash from 'lodash';
import CPF from 'cpf-check';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  useVendorFetcher,
  useHubFetcher,
  isFieldDisabled,
  renderHubCodeSelect,
  generateDefaultLabels,
  generateFieldListMap,
} from '../../../common/AddDriverVehicleUtils';

const { Option } = Select;

const AddNewDriverModal : React.FC<AddNewDriverModalProps> = (props: AddNewDriverModalProps) => {
  const [form] = Form.useForm();
  const [addressHierarchyOptions, setAddressHierarchyOptions] = React.useState<Array<any>>([]);
  const [topHierarchy, setTopHierarchy] = React.useState(null);
  const [idDocsUrl, setIdDocsUrl] = React.useState<IdentificationDocument[]>([]);
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = React.useState<
    { label: string; value: string } | undefined
  >();
  const { classes, handleModalClose, isVisible, action, driverData, fields_list,
    allowed_jfl_worker_types, useAddressHierarchyForLocationFields,
    workerValidationStrategy, autoGenerateWorkerDetails, reloadView } = props;
  const [validationRules, setValidationRules] = React.useState({});
  const [nestedFieldsList, setNestedFieldsList] = React.useState<Array<any>>([]);
  const [nestedRules, setNestedRules] = React.useState({});
  const [hubQuery, setHubQuery] = React.useState<string>('');
  const debouncedHubQuery = useDebounce(hubQuery, 500);
  const [addressFieldsLoading, setAddressFieldsLoading] = React.useState({});
  const [existingRiderDetails, setExistingRiderDetails] = React.useState({} as FetchRiderDetails);
  const [addressHierarchyLocationFields,
  setAddressHierarchyLocationFields] = React.useState<Array<any>>([]);
  const { t } = useTranslation();

  // make vendor data and hub data fetch function generic because used in both vehicle and driver
  const { vendorNames, vendorLoading, fetchVendorList } = useVendorFetcher(form, t, 'vendor_code');
  const { hubOptions } = useHubFetcher(debouncedHubQuery, t);

  const defaultLabels = generateDefaultLabels(addDriverFieldsList);
  const fieldList = generateFieldListMap(fields_list);

  const docList = fieldList["identification_documents"]?.required_documents || [];
  const showCountryCodePrefix = fieldList['phone']?.show_country_code_prefix || false;

  const setVendorCode = (workerVendorName: string) => {
    if (!workerVendorName || !vendorNames?.length) return;
    const vendor = vendorNames.find((vendor) => vendor.name === workerVendorName);
    if (vendor) {
      form.setFieldsValue({ vendor_code: vendor.code });
      setSelectedVendor({
        label: vendor.code,
        value: vendor.id,
      });
    }
  };

  const handleVendorChange = (value: string) => {
    const vendor = vendorNames.find((v) => v.id === value);
    if (vendor) {
      const vendorObj = {
        label: vendor.code,
        value: vendor.id,
      };
      setSelectedVendor(vendorObj);
      form.setFieldsValue({ vendor_code: vendor.code });
    }
  };

 const handleAutofills = (fieldName: string, newValue: any, form: any) => {
    if (fieldList[fieldName]?.autofills) {
      const autofillFields = fieldList[fieldName].autofills;
      const autofillUpdates = autofillFields.reduce((acc, field) => {
        acc[field] = newValue;
        return acc;
      }, {});
      form.setFieldsValue(autofillUpdates);
    }
  };

  const getRiderDetails = async () => {
    if (action !== 'edit' || !driverData?.id) {
      return;
    }
    const response = await fetchDriverInfo({ workerId: driverData?.id });
    if (!response.isSuccess) {
      message.error(response.errorMessage);
      return;
    }
    const response_data = response?.data[0] || {};
    let { phone } = response_data;
      const phoneField = fieldList['phone'];
      // for backward compatibility, if the phone number does not have a country code
      // add the default country code
      if (phone && !phone.startsWith('+') && showCountryCodePrefix && phoneField?.default_country_code) {
        const countryDialCode = getCountryCallingCode(phoneField.default_country_code);
        phone = `+${countryDialCode}${phone}`;
      }
    const formValues = {
      name: response_data.name,
      worker_code: response_data.worker_code,
      phone,
      driving_licence_number: response_data.driving_licence_number,
      driving_licence_validity: response_data.driving_licence_validity
      ? moment(response_data.driving_licence_validity)
      : null,
      city: response_data.city,
      country: response_data.country,
      pincode: response_data.pincode,
      gender: response_data.gender,
      state: response_data.state,
      username: response_data.username,
      license_category_list: response_data.license_category_list,
      available_for_trip_action: response_data.available_for_trip_action || undefined,
      jfl_worker_type: response_data.worker_type,
      hub_code: response_data.hub_code,
    };
    form.setFieldsValue(formValues);
    Object.entries(formValues).forEach(([key, value]) => {
      if (value) {
        handleAutofills(key, value, form);
      }
    });
    // Handling Vendor Code
    setVendorCode(response_data?.worker_vendor_name);
    setExistingRiderDetails(response_data as any);
    // Handling Identification Documents
    const identificationDocs = response_data?.extra_details?.identification_documents || [];
    setIdDocsUrl(identificationDocs.map((doc) => ({
      ...doc,
      type: `${doc.type}_image`,
    })));
    identificationDocs.forEach((doc) => {
      form.setFieldsValue({
        [`${doc.type}_number`]: doc.number,
      });
    });

    const identificationFormValues = {};
    identificationDocs.forEach((doc) => {
      const docNumberField = `${doc.type}_number`;
      identificationFormValues[docNumberField] = doc.number;
      handleAutofills(docNumberField, doc.number, form);
    });
    form.setFieldsValue(identificationFormValues);
  };

  const formatPhoneNumber = (value) => {
    if (!value) return null;

    if (!showCountryCodePrefix) return value;

    // PhoneInput returns the value without the + icon
    const parsedPhoneNumber = parsePhoneNumberFromString(value);
    if (!parsedPhoneNumber?.countryCallingCode) {
      return `+${value}`;
    }
    return value;
  };

  const renderWorkerIsActive = (label, type) => {
    const disabled = isFieldDisabled(type, action);
    return (
      <Radio.Group disabled={disabled}>
        <Radio value>{t('Yes')}</Radio>
        <Radio value={false}>{t('No')}</Radio>
      </Radio.Group>
    );
  };
  const getAddressHierarchyMetadata = async () => {
    const response = await fetchAddressMetadata();
    if (response.isSuccess) {
      const addressHierarchyMetadata = response.data;
      const { hierarchy } = addressHierarchyMetadata;
      if (Array.isArray(hierarchy) && hierarchy.length) {
        let locationKey = '';
        const topHierarchy = hierarchy.find((item) => item.id === 'L1');
        if (topHierarchy) {
          locationKey = topHierarchy.location_key;
        }
        const hierarchyFields = hierarchy.map((h) => h.location_key);
        setTopHierarchy(locationKey);
        setAddressHierarchyLocationFields(hierarchyFields);
      }
    } else {
      message.error(response.errorMessage);
    }
  };

  const handleAddressFieldChange = async (entry) => {
    const payload = {
      location_key: topHierarchy,
      code: entry?.value,
    };

    const response = await getBottomUpHierarchyForAddressNode(payload);
    if (response.isSuccess) {
      const { data } = response;
      const hierarchyKeys = Object.keys(data) || [];
      const newFieldValuesMapping = {};
      hierarchyKeys.forEach((key) => {
        if (!addRiderLocationFields.includes(key)) {
          return;
        }
        const code = lodash.get(data, `${key}.code`) || null;
        const name = lodash.get(data, `${key}.name`) || null;
        if (code) {
          newFieldValuesMapping[key] = {
            label: `${name} - ${code}`,
            value: code,
          };
        }
      });
      form.setFieldsValue({
        ...newFieldValuesMapping,
      });
    }
  };

  const addressField = existingRiderDetails[topHierarchy];
  const fetchAddressDetailsForEditRider = async () => {
    if (useAddressHierarchyForLocationFields && topHierarchy && addressField) {
      form.setFieldsValue({
        [topHierarchy]: {
          value: addressField,
        },
      });
      await handleAddressFieldChange({
        value: addressField,
      });
    }
  };

  React.useEffect(() => {
    fetchAddressDetailsForEditRider();
  }, [topHierarchy, addressField]);

  const cpfValidator = (strategy, field) => {
    const validator = (_, value) => {
      if (value && !CPF.validate(value)) {
        return Promise.reject(new Error(strategy.error || t('Invalid CPF Number')));
      }
      return Promise.resolve();
    };
    return validator;
  };

  const fixedListValidator = (strategy, field) => {
    const fieldListToVerify = strategy.fixed_list || [];
    const validator = (_, value) => {
      if (fieldListToVerify.length && !fieldListToVerify.includes(value)) {
        return Promise.reject(new Error(strategy.error || t('Invalid value')));
      }
      return Promise.resolve();
    };
    return validator;
  };

  const externalStrategyValidators = {
    cpf: cpfValidator,
    fixedList: fixedListValidator,
  };
  const generateRulesFromStrategy = (strategy, field) => {
    const rulesToSet = [];
    switch (strategy?.strategy_type) {
      case 'regex': {
        const regex = new RegExp(strategy.regex);
        rulesToSet.push({
          pattern: regex,
          message: strategy.error || 'Invalid format!',
        });
        break;
      }
      case 'fixed_list': {
        const ruleHandler = externalStrategyValidators['fixedList'];
        const validatorFunction = ruleHandler(strategy, field);
        rulesToSet.push({ validator: validatorFunction });
        break;
      }
      case 'external': {
        const externalRuleHandler = externalStrategyValidators[strategy.executor];
        const externalValidatorFunction = externalRuleHandler(strategy, field);
        rulesToSet.push({ validator: externalValidatorFunction });
        break;
      }
      default:
        break;
    }
    return rulesToSet;
  };
  const generateRulesFromValidationStrategy = () => {
    const fields = Object.keys(workerValidationStrategy);
    const validationRulesToSet = {};
    const nestedFieldsToSet = [];
    fields.forEach((field) => {
      const strategy = workerValidationStrategy[field];
      if (strategy?.strategy_type === 'input_filter') {
        nestedFieldsToSet.push(field);
      } else {
        const rulesToSet = generateRulesFromStrategy(strategy, field);
        validationRulesToSet[field] = rulesToSet;
      }
    });
    setValidationRules(validationRulesToSet);
    setNestedFieldsList(nestedFieldsToSet);
  };
  const generateNestedRules = (strategy, field) => {
    if (strategy?.strategy_type !== 'input_filter') {
      return generateRulesFromStrategy(strategy, field);
    }

    const referenceKey = strategy?.reference_key;
    if (!referenceKey) {
      return null;
    }

    const fieldValue = form.getFieldValue(referenceKey);
    if (!fieldValue) {
      return null;
    }

    let value = fieldValue;
    if (typeof fieldValue === 'object') {
      value = fieldValue?.value;
    }

    const subStrategy = lodash.get(strategy, value) || null;
    if (!subStrategy) {
      return null;
    }
    return generateNestedRules(subStrategy, field);
  };

  React.useEffect(() => {
    fetchVendorList();
    generateRulesFromValidationStrategy();
    if (useAddressHierarchyForLocationFields) {
      getAddressHierarchyMetadata();
    }
  }, []);
  React.useEffect(() => {
    if (!vendorLoading) {
      getRiderDetails();
    }
  }, [vendorLoading, driverData, action]);

  const renderTextInput = (value: string, type: string) => {
    const disabled = isFieldDisabled(type, action);
    return <Input placeholder={value} disabled={disabled} />;
  };

  const renderPassword = (value: string, type: string) => {
    const disabled = isFieldDisabled(type, action);
    return <Input.Password placeholder={value} autoComplete="new-password" disabled={disabled} />;
  };

  const renderPhone = (value: string, type: string) => {
    const disabled = isFieldDisabled(type, action);
    return <Input type="number" placeholder={value} disabled={disabled} />;
  };

  const renderDate = (value: string, type: string) => {
    const disabled = isFieldDisabled(type, action);
    return (
      <DatePicker
        className={classes?.date}
        placeholder={t(`Select ${value}`)}
        disabled={disabled}
        format="YYYY/MM/DD"
      />
    );
  };

  const getRules = (field, label) => {
    const defaultRules = [];
    const { key } = field;
    const isRequired = (!field.is_optional) && !(action === 'edit' && field.key === 'password');
    if (isRequired) {
      defaultRules.push({ required: true, message: t(`${label} is required!`) });
    }
    if (field.regex) {
      const regexPattern = new RegExp(field.regex);
      defaultRules.push({
        pattern: regexPattern,
        message: t(`${label} is invalid format.`),
      });
    }
    const rules = validationRules[key] || [];
    if (Array.isArray(rules) && rules.length) {
      defaultRules.push(...rules);
    }

    const additionalRules = nestedRules[key];
    if (Array.isArray(additionalRules) && additionalRules.length) {
      defaultRules.push(...additionalRules);
    }

    if (field.key === 'phone') {
      // Add phone validation logic
      defaultRules.push({
        validator(_, value) {
          // Format and validate phone number
          const formattedValue = formatPhoneNumber(value);
          if (
            !formattedValue ||
            !showCountryCodePrefix ||
            (showCountryCodePrefix && isValidMobilePhoneNumber(formattedValue))
          ) {
            return Promise.resolve();
          }
          return Promise.reject(new Error(t(`${label} is invalid!`)));
        },
      });
    }
    return defaultRules;
  };

  const updateDocs = (prevIdDocsUrl: any[], fieldItemKey: string, url: string, form: any,
    signedUrl: string) => {
    const existingDocIndex = prevIdDocsUrl.findIndex((doc) => doc.type === `${fieldItemKey}_image`);
    let updatedDocs;
    if (existingDocIndex !== -1) {
      updatedDocs = prevIdDocsUrl.map((doc, index) => {
        if (index === existingDocIndex) {
          return { type: `${fieldItemKey}_image`, image: url, signedUrl };
        }
        return doc;
      });
    } else {
      updatedDocs = [...prevIdDocsUrl, { type: `${fieldItemKey}_image`, image: url, signedUrl }];
    }
    form.setFieldsValue({
      [`${fieldItemKey}_image`]: url,
    });
    return updatedDocs;
  };

  const triggerValidator = (field) => {
    const strategy = workerValidationStrategy[field];
    const rules = generateNestedRules(strategy, field);
    let newNestedRules = {
      [field]: [],
    };
    if (Array.isArray(rules) && rules.length) {
      newNestedRules = {
        ...nestedRules,
        [field]: rules,
      };
    }
    setNestedRules(newNestedRules);
  };

  const handleClick = (field) => {
    if (nestedFieldsList.includes(field)) {
      triggerValidator(field);
    }
  };

  const fetchAndRenderSignedUrl : any = async (url): Promise<any> => {
    const response = await getSignedUrlApi(url);
    const respSignedUrl: string = response?.data?.signedUrl ?? '';
    if (!response.isSuccess) {
      message.error(response.errorMessage);
      return "";
    }
    if (!respSignedUrl) {
      message.error(t('Unable to retrieve signed Url'));
      return "";
    }
    return respSignedUrl;
  };

  const ImageUploader: React.FC<ImageUploaderProps> = (props: ImageUploaderProps) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const { fieldItem } = props;
    const existingImage = idDocsUrl?.find((item) => item.type === `${fieldItem?.key}_image`);
    const imageUrl = existingImage?.image;
    let signedUrl = existingImage?.signedUrl || imageUrl;
    const onChangeAction = async (changeState) => {
      setIsLoading(true);
      const file = changeState?.file;
      if (!file) {
        message.error(t('File not found'));
        setIsLoading(false);
        return;
      }
      const response = await getS3SignedUrlAndUploadImage(file);
      if (!response.isSuccess) {
        message.error(response?.errorMessage ?? t('Unable to upload image'));
        setIsLoading(false);
        return;
      }
      if (!('url' in response) || !response.url) {
        message.error(t('Unable to upload image please try later'));
        setIsLoading(false);
        return;
      }
      const { url } = response;
      if (url) {
        signedUrl = await fetchAndRenderSignedUrl(url);
        setIdDocsUrl((prevIdDocsUrl) =>
          updateDocs(prevIdDocsUrl, fieldItem.key, url, form, signedUrl));
        message.success(t('Image upload completed'));
        setIsLoading(false);
      }
    };
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <Form.Item
          name={`${fieldItem.key}_image`}
          label=''
          rules={[
            {
              required: fieldItem?.is_proof_mandatory && !imageUrl,
              message: t(`Please upload image`),
            },
          ]}
        >
          <Upload onChange={onChangeAction} beforeUpload={() => false}>
            <Button>
              {isLoading ? (
                <LoadingOutlined style={{ fontSize: 18 }} spin />
              ) : (
                <PaperClipOutlined />
              )}
              {t('Upload Image')}
            </Button>
          </Upload>
        </Form.Item>
        {signedUrl &&
          <img style={{ height: '90px', width: '110px' }} src={signedUrl} alt='Image' />}
      </div>
    );
  };

  const handleHubSearch = (value: string) => {
    setHubQuery(value);
  };

  const getIdentificationDocsItem = (item: any) => {
    const type = item.type || undefined;
    return (
      <>
        <Col span={12}>
          <Form.Item
            label={`${item.label}`}
            name={`${item.key}_number`}
            colon={false}
            rules={getRules(item, item.label)}
          >
            {renderTextInput(`${item.label}`, type)}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            rules={getRules(item, item.label)}
            colon={false}
          >
            <ImageUploader fieldItem={item} />
          </Form.Item>
        </Col>
      </>
    );
  };
  const getWorkerVenderSelect = (label: string, type: string) => {
    const disabled = isFieldDisabled(type) || (action === 'edit');
    if (vendorLoading) {
      return (
        <Input
          placeholder={t("loading_vendors_dot")}
          disabled
        />
      );
    }
    const vendorOptions = vendorNames?.map((vendor) => ({
      label: vendor.code,
      value: vendor.id,
    })) || [];

    return (
      <Select
        value={selectedVendor?.value}
        onChange={handleVendorChange}
        placeholder={t('Select Vendor Code')}
        showSearch
        showArrow
        disabled={disabled}
        style={{ width: '225px' }}
        options={vendorOptions}
      />
    );
  };

  const generateOptions = (key: string, items: any[]) => {
    if (key === 'gender') {
      return items.map((d: any) => ({
        value: d?.value,
        label: d?.key,
      }));
    }
    return items.map((d: any) => ({
      value: d,
      label: d,
    }));
  };

  const renderSelect = (key: string, label: string, list: any[], type: string,
    isMultiSelect: boolean = false) => {
    const disabled = isFieldDisabled(type, action);
    return (
      <Select
        mode={isMultiSelect ? 'multiple' : undefined}
        showSearch
        placeholder={t(`Select ${label}`)}
        defaultActiveFirstOption={false}
        showArrow
        disabled={disabled}
        notFoundContent={null}
        options={generateOptions(key, list)}
      />
    );
  };

  const handleAddressFieldSearch = async (name, queryString: string) => {
    setAddressFieldsLoading({
      ...addressFieldsLoading,
      [name]: true,
    });
    const payload = {
      location_key: name,
      code: queryString,
    };
    const response = await getAddressHierarchyData(payload);
    if (response.isSuccess) {
      const { data } = response;
      const options = data?.map((item) => {
        return {
          value: item.code,
          label: `${item.name} - ${item.code}`,
        };
      });
      setAddressHierarchyOptions(options);
    }
    setAddressFieldsLoading({
      ...addressFieldsLoading,
      [name]: false,
    });
  };

  const debouncedAddressFieldsSearch = lodash.debounce(async (name, queryString) => {
    const trimmedString = queryString.trim();
    if (trimmedString.length < 3) {
      return;
    }
    handleAddressFieldSearch(name, trimmedString);
  }, 300);

  const renderAddressFieldsDropdown = (field, label, type) => {
    const { key } = field;
    const disabled = isFieldDisabled(type, action);
    return (
      <Select
        showSearch
        allowClear
        onSearch={(q) => debouncedAddressFieldsSearch(key, q)}
        onChange={handleAddressFieldChange}
        filterOption={false}
        options={addressHierarchyOptions}
        loading={addressFieldsLoading[key] || false}
        labelInValue
        disabled={!(
          useAddressHierarchyForLocationFields &&
          key === topHierarchy
        ) || disabled}
      />
    );
  };
  const renderPhoneWithCountryCode = (value, field, type) => {
    const disabled = isFieldDisabled(type, action);
    return (
      <PhoneInput
        inputStyle={{ width: '225px' }}
        dropdownStyle={{ width: '225px' }}
        enableSearch
        country={field?.default_country_code?.toLowerCase()}
        placeholder={value}
        disabled={disabled}
      />
    );
  };
  const renderPhoneField = (fieldToShow, label, type) => {
    const isShowCountryPrefix = fieldToShow?.show_country_code_prefix || false;
    return isShowCountryPrefix
      ? renderPhoneWithCountryCode(label, fieldToShow, type)
      : renderPhone(label, type);
  };

  const getFormItem = (fieldToShow: any, label: string) => {
    const type = fieldToShow.type || undefined;
    switch (fieldToShow.key) {
      case 'username':
      case 'name':
        return renderTextInput(label, type);
      case 'city':
      case 'state':
      case 'pincode':
      case 'country':
        if (useAddressHierarchyForLocationFields && topHierarchy
          && addressHierarchyLocationFields.includes(fieldToShow.key)
        ) {
          return renderAddressFieldsDropdown(fieldToShow, label, type);
        }
        return renderTextInput(label, type);
      case 'driving_licence_number':
      case 'worker_code':
        return renderTextInput(label, type);
      case 'hub_code':
        return renderHubCodeSelect(hubOptions, handleHubSearch,
          isFieldDisabled(type, action), t, false);
      case 'vendor_code':
        return getWorkerVenderSelect(label, type);
      case 'password':
        return renderPassword(label, type);
      case 'phone':
        return renderPhoneField(fieldToShow, label, type);
      case 'driving_licence_validity':
        return renderDate(label, type);
      case 'available_for_trip_action':
        return renderWorkerIsActive(label, type);
      case 'gender':
        return renderSelect("gender", label, genderList, type);
      case 'jfl_worker_type':
        return renderSelect("jfl_worker_type", label, allowed_jfl_worker_types, type);
      case 'license_category_list':
        return renderSelect("license_category_list", label, fieldToShow["value_list"], type, true);
      default:
        return null;
    }
  };
  const sanitizeData = (values: any, idDocsUrl: any[], fieldList: any) => {
    const sanitizedValues: any = { ...values };
    Object.keys(sanitizedValues).forEach((key) => {
      if (typeof sanitizedValues[key] === 'string') {
        sanitizedValues[key] = sanitizedValues[key].trim();
      }
    });
    if (selectedVendor?.label) {
      sanitizedValues.vendor_code = selectedVendor.label;
    }
    if (sanitizedValues.driving_licence_validity) {
      sanitizedValues.driving_licence_validity = sanitizedValues.driving_licence_validity.format('YYYY-MM-DD');
    }
    const docList = fieldList?.identification_documents?.required_documents || [];
    const identificationDocuments = [];

    docList.forEach((doc) => {
      const docNumber = sanitizedValues[`${doc.key}_number`];
      const existingDoc = idDocsUrl.find((item) => item.type === `${doc.key}_image`);
      if (docNumber || existingDoc?.image) {
        identificationDocuments.push({
          type: doc.key,
          number: docNumber || '',
          image: existingDoc?.image || '',
        });
      }
    });
    if (identificationDocuments.length > 0) {
      sanitizedValues.identification_documents = identificationDocuments;
    } else {
      sanitizedValues.identification_documents = undefined;
    }
    Object.keys(sanitizedValues).forEach((key) => {
      if (!sanitizedValues[key]) {
        sanitizedValues[key] = undefined;
      }
    });
    return sanitizedValues;
  };
  const onFinish = async (values: any) => {
    if (Array.isArray(nestedFieldsList) && nestedFieldsList.length) {
      nestedFieldsList.forEach((field) => {
        triggerValidator(field);
      });
    }
    try {
      await form.validateFields();
      const mandatoryImageDocs = docList?.filter((doc) => doc.is_proof_mandatory) || [];
      if (Array.isArray(mandatoryImageDocs) && mandatoryImageDocs.length) {
        mandatoryImageDocs.forEach((doc) => {
          const imageDoc = idDocsUrl?.find((item) => item.type === `${doc.key}_image`);
          if (!imageDoc || !imageDoc.image) {
            throw new Error(`${doc.label} Image is mandatory.`);
          }
        });
      }
    } catch (err) {
      message.error(err.message || t('Please check the errors'));
      return;
    }
    const sanitizedValues = sanitizeData(values, idDocsUrl, fieldList);
    const body = {
      ...sanitizedValues,
      driving_licence_validity: sanitizedValues.driving_licence_validity || undefined,
      available_for_trip_action: sanitizedValues.available_for_trip_action || undefined,
    };
    if (body && body['phone']) {
      body['phone'] = formatPhoneNumber(body['phone']);
    }
    addRiderLocationFields.forEach((entry) => {
      if (typeof values[entry] === 'object') {
        body[entry] = values[entry]?.value;
      } else {
        body[entry] = values[entry];
      }
    });
    setConfirmLoading(true);
    const response =
      action === 'create'
        ? await addDriver(body)
        : await updateDriver(driverData?.id, body);
    if (response.isSuccess) {
      if (action === 'create') {
        if (response.data.isApproval) {
          message.success(t('Driver creation sent for approval'));
        } else {
          message.success(t('Driver Successfully created'));
        }
      } else {
        message.success(t('Driver details Updated successfully'));
      }
      handleModalClose();
      reloadView();
    } else {
      message.error(response?.errorMessage || "`Error in creating/updating driver");
    }
    setConfirmLoading(false);
  };

  const onValuesChange = (changedValues: Record<string, any>) => {
    const fieldUpdated = Object.keys(changedValues)[0];
    const newValue = changedValues[fieldUpdated];
    handleAutofills(fieldUpdated, newValue, form);

    const identificationDocs = fieldList["identification_documents"]?.required_documents;
    if (identificationDocs) {
      identificationDocs.forEach((doc) => {
        const docFieldName = `${doc.key}_number`;
        if (fieldUpdated === docFieldName && doc.autofills) {
          const autofillUpdates = doc.autofills.reduce((acc, field) => {
            acc[field] = newValue;
            return acc;
          }, {});
          form.setFieldsValue(autofillUpdates);
        }
      });
    }
  };
  return (
    <Modal
      visible={isVisible}
      onCancel={handleModalClose}
      maskClosable={false}
      footer={null}
      title={t(action === 'edit' ? t('Update Rider') : t('Add Rider'))}
      width={1000}
    >
      <Form
        form={form}
        initialValues={{
          status: 'active',
          jfl_worker_type: action !== 'edit' && allowed_jfl_worker_types.length && allowed_jfl_worker_types[0],
        }}
        {...addRiderFormItemLayout}
        className={classes.formContainer}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Row>
          {Object.entries(fieldList).map(([key, item]) => {
            // Skip 'identification_documents'
            if (key === 'identification_documents') {
              return null;
            }
            const label = item["label"] || defaultLabels[item["key"]] || item["key"];
            return (
              <Col span={12} key={key}>
                <div onClick={() => handleClick(key)}>
                  <Form.Item
                    label={t(label)}
                    name={key}
                    colon={false}
                    rules={getRules(item, label)}
                  >
                    {getFormItem(item, label)}
                  </Form.Item>
                </div>
              </Col>
            );
          })}
        </Row>
        <Row>
          {docList?.length > 0 && (
            <Row style={{ marginBottom: 10 }}>
              <h3>
                <b>{t('Identification Documents')}</b>
              </h3>
            </Row>
          )}
        </Row>
        <Row>
          {docList?.map((item) => {
            return (
              <React.Fragment key={item.key}>
                {getIdentificationDocsItem(item)}
              </React.Fragment>
            );
          })}
        </Row>
      </Form>
      <Form.Item {...addRiderTailLayout} className={classes?.actionButtons}>
        <Button onClick={handleModalClose} className={classes?.cancelButton}>
          {t('Cancel')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={confirmLoading}
          onClick={() => form.submit()}
        >
          {t('Submit')}
        </Button>
      </Form.Item>
    </Modal>
  );
};
const mapStateToProps = ({ masterData }) => {
  const { vendor_config, on_demand_management } = masterData;
  const { worker_master_config } = vendor_config;
  const allowed_jfl_worker_types =
    on_demand_management.allowed_jfl_worker_types || [];
  return {
    fields_list: worker_master_config?.fields_list || {},
    useAddressHierarchyForLocationFields:
      worker_master_config?.use_address_hierarchy_for_location_fields || false,
    autoGenerateWorkerDetails: worker_master_config?.auto_generate_worker_details,
    allowed_jfl_worker_types,
    workerValidationStrategy: worker_master_config?.validation_strategy || {},
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: AddDriverStyle,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(AddNewDriverModal);
